/* ==========================================================================
   DISAVE — shared front-end behaviour (redesign 2026)
   Cart add + toast, wishlist, quick view, size chips on cards, tab groups.
   Vanilla JS, no dependencies. Safe to load on every page.
   ========================================================================== */

(function () {
  'use strict';

  // Every dsv-* section renders the same <script src>. Shopify does not dedupe
  // those, so bail out if we already wired the document up.
  if (window.__dsvDesignInit) return;
  window.__dsvDesignInit = true;

  var ROUTES = window.dsvRoutes || {};
  var STRINGS = window.dsvStrings || {};

  function t(key, fallback) {
    return (STRINGS && STRINGS[key]) || fallback;
  }

  /* ---------------------------------------------------------------- toast */

  var toastEl = null;
  var toastTimer = null;

  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'dsv-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    // Force reflow so repeated calls re-trigger the transition.
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2600);
  }

  /* ----------------------------------------------------------- cart adds */

  function refreshCartCount() {
    return fetch((ROUTES.cart || '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-dsv-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        // Let the host theme (Wonder) update its own drawer/bubble if it listens.
        document.dispatchEvent(new CustomEvent('dsv:cart:updated', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        // Wonder re-renders its drawer + header bubble only on this event.
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
        return cart;
      })
      .catch(function () { /* count is cosmetic — never block the add */ });
  }

  function setBusy(button, busy) {
    if (!button) return;
    if (busy) {
      button.dataset.dsvLabel = button.innerHTML;
      button.setAttribute('aria-disabled', 'true');
      button.innerHTML = '<span class="dsv-spinner"></span>';
    } else if (button.dataset.dsvLabel) {
      button.innerHTML = button.dataset.dsvLabel;
      button.removeAttribute('aria-disabled');
      delete button.dataset.dsvLabel;
    }
  }

  function addToCart(variantId, quantity, button, properties) {
    if (!variantId) {
      toast(t('selectVariant', 'Izberi velikost'));
      return Promise.resolve(null);
    }
    setBusy(button, true);
    var body = { items: [{ id: Number(variantId), quantity: quantity || 1 }] };
    if (properties && Object.keys(properties).length) body.items[0].properties = properties;

    return fetch((ROUTES.cartAdd || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        setBusy(button, false);
        if (!res.ok) {
          toast(res.data && res.data.description ? res.data.description : t('addError', 'Napaka pri dodajanju'));
          return null;
        }
        toast(t('added', '✓  Dodano v košarico'));
        refreshCartCount();
        return res.data;
      })
      .catch(function () {
        setBusy(button, false);
        toast(t('addError', 'Napaka pri dodajanju'));
        return null;
      });
  }

  window.dsvAddToCart = addToCart;
  window.dsvToast = toast;

  /* --------------------------------------------------------- delegation */

  document.addEventListener('click', function (event) {
    /* Add to cart ------------------------------------------------------- */
    var addBtn = event.target.closest('[data-dsv-add]');
    if (addBtn) {
      event.preventDefault();
      var scope = addBtn.closest('[data-dsv-product]') || document;
      var selected = scope.querySelector('[data-dsv-size].is-active');
      var variantId = addBtn.getAttribute('data-dsv-add') ||
        (selected && selected.getAttribute('data-variant')) ||
        (scope.querySelector('[data-dsv-variant-id]') || {}).value;
      var qtyInput = scope.querySelector('[data-dsv-qty-value]');
      var qty = qtyInput ? parseInt(qtyInput.textContent || qtyInput.value, 10) || 1 : 1;
      addToCart(variantId, qty, addBtn);
      return;
    }

    /* Wishlist (local only — no app dependency) ------------------------- */
    var wish = event.target.closest('[data-dsv-wish]');
    if (wish) {
      event.preventDefault();
      var id = wish.getAttribute('data-dsv-wish');
      var store = readWishlist();
      var idx = store.indexOf(id);
      if (idx === -1) { store.push(id); } else { store.splice(idx, 1); }
      writeWishlist(store);
      wish.setAttribute('aria-pressed', idx === -1 ? 'true' : 'false');
      return;
    }

    /* Size chips on cards ---------------------------------------------- */
    var chip = event.target.closest('[data-dsv-size]');
    if (chip) {
      event.preventDefault();
      var chipScope = chip.closest('[data-dsv-product]');
      if (chipScope) {
        chipScope.querySelectorAll('[data-dsv-size]').forEach(function (c) {
          c.classList.toggle('is-active', c === chip);
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        var priceEl = chipScope.querySelector('[data-dsv-price]');
        var price = chip.getAttribute('data-price');
        if (priceEl && price) priceEl.textContent = price;
      }
      return;
    }

    /* Tab groups -------------------------------------------------------- */
    var tab = event.target.closest('[data-dsv-tab]');
    if (tab) {
      event.preventDefault();
      var group = tab.closest('[data-dsv-tabs]');
      if (group) {
        var target = tab.getAttribute('data-dsv-tab');
        group.querySelectorAll('[data-dsv-tab]').forEach(function (b) {
          b.classList.toggle('is-active', b === tab);
          b.setAttribute('aria-selected', b === tab ? 'true' : 'false');
        });
        group.querySelectorAll('[data-dsv-panel]').forEach(function (p) {
          p.hidden = p.getAttribute('data-dsv-panel') !== target;
        });
      }
      return;
    }

    /* Accordions -------------------------------------------------------- */
    var acc = event.target.closest('[data-dsv-accordion]');
    if (acc) {
      event.preventDefault();
      var panel = document.getElementById(acc.getAttribute('aria-controls'));
      var open = acc.getAttribute('aria-expanded') === 'true';
      // Single-open behaviour within the same accordion list.
      var list = acc.closest('[data-dsv-accordion-list]');
      if (list && !open) {
        list.querySelectorAll('[data-dsv-accordion]').forEach(function (other) {
          if (other === acc) return;
          other.setAttribute('aria-expanded', 'false');
          var op = document.getElementById(other.getAttribute('aria-controls'));
          if (op) op.hidden = true;
          var os = other.querySelector('[data-dsv-accordion-sym]');
          if (os) os.textContent = '+';
        });
      }
      acc.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (panel) panel.hidden = open;
      var sym = acc.querySelector('[data-dsv-accordion-sym]');
      if (sym) sym.textContent = open ? '+' : '−';
      return;
    }
  });

  /* -------------------------------------------------------- wishlist io */

  function readWishlist() {
    try { return JSON.parse(localStorage.getItem('dsv:wishlist') || '[]'); }
    catch (e) { return []; }
  }
  function writeWishlist(list) {
    try { localStorage.setItem('dsv:wishlist', JSON.stringify(list)); } catch (e) { /* private mode */ }
  }

  function hydrateWishlist() {
    var store = readWishlist();
    document.querySelectorAll('[data-dsv-wish]').forEach(function (el) {
      el.setAttribute('aria-pressed', store.indexOf(el.getAttribute('data-dsv-wish')) !== -1 ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------- recently viewed io */

  function pushRecentlyViewed(handle) {
    if (!handle) return;
    try {
      var seen = JSON.parse(localStorage.getItem('dsv:seen') || '[]');
      seen = [handle].concat(seen.filter(function (h) { return h !== handle; })).slice(0, 6);
      localStorage.setItem('dsv:seen', JSON.stringify(seen));
    } catch (e) { /* noop */ }
  }
  window.dsvPushRecentlyViewed = pushRecentlyViewed;

  /* ------------------------------------------------------------- init */

  function init() {
    hydrateWishlist();
    refreshCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-hydrate after Shopify theme-editor section reloads.
  document.addEventListener('shopify:section:load', hydrateWishlist);
})();
